<template>
  <div class="wishlist">
    <div class="wishlist__inner" v-if="productsInWishlist.length">
      <h6 class="wishlist__title">{{ t('profile.wishlist.title') }}</h6>
      <div class="wishlist__main">
        <div class="wishlist__main-top">
          <p>{{ t('profile.wishlist.product') }}</p>
          <p>{{ t('profile.wishlist.price') }}</p>
        </div>
        <div class="wishlist__main-list">
          <div
              v-for="(product, idx) in productsInWishlist"
              :key="product.node.uuid"
              class="wishlist__item"
          >
            <div>
              <ui-filter-checkbox
                  v-model="selectedProducts[idx]"
                  :id="idx"
              />
              <img class="wishlist__item-image" :src="product.node.images.edges[0]?.node.image" :alt="product.node.name">
              <h6>{{ product.node.name }}</h6>
            </div>
            <div>
              <p><img src="@icons/currency.svg" alt="currency"> {{ product.node.price }}</p>
              <svg @click="removeProductFromWishlist(product.node.uuid)" xmlns="http://www.w3.org/2000/svg" width="27" height="25" viewBox="0 0 27 25" fill="none">
                <path d="M1.89062 6.61523L3.64725 23.302C3.68388 23.6503 3.84822 23.9728 4.10857 24.2072C4.36892 24.4416 4.70684 24.5713 5.05715 24.5713H21.4054C21.7557 24.5713 22.0936 24.4416 22.354 24.2072C22.6143 23.9728 22.7787 23.6503 22.8153 23.302L24.5719 6.61523H1.89062ZM16.539 19.4597L13.2313 16.152L9.92359 19.4597L8.41977 17.9559L11.7275 14.6482L8.41977 11.3405L9.92359 9.83669L13.2313 13.1444L16.539 9.83669L18.0428 11.3405L14.7351 14.6482L18.0428 17.9559L16.539 19.4597Z" fill="currentColor"/>
                <path d="M25.7527 0H0.708791C0.317337 0 0 0.317337 0 0.708791V4.01648C0 4.40794 0.317337 4.72528 0.708791 4.72528H25.7527C26.1442 4.72528 26.4615 4.40794 26.4615 4.01648V0.708791C26.4615 0.317337 26.1442 0 25.7527 0Z" fill="currentColor"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="wishlist__main-bottom">
          <div>
            <h6>{{ t('profile.cart.total') }}: </h6>
            <p><img src="@icons/currency.svg" alt="currency"> {{ totalPrice }}</p>
          </div>
          <ui-button class="wishlist__button" @click="addToCart">{{ t('buttons.addToCart') }}</ui-button>
        </div>
      </div>
    </div>
    <div class="wishlist__empty" v-else>
      <p>{{ t('profile.wishlist.empty') }}</p>
      <router-link :to="translations.i18nRoute({ name: 'store' })"><ui-button>{{ t('buttons.goStore') }}</ui-button></router-link>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {computed, ref} from "vue";
import UiButton from "@/components/ui/ui-button.vue";
import {useAuthStore} from "@/stores/auth.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {Wishlist} from "@/core/api/wishlist.js";
import translations from "@/core/helpers/translations.js";
import UiFilterCheckbox from "@/components/ui/ui-filter-checkbox.vue";
import {Orders} from "@/core/api/orders.js";
import {useCartStore} from "@/stores/cart.js";
import {ElNotification} from "element-plus";

const {t} = useI18n();
const wishlistStore = useWishlistStore()
const authStore = useAuthStore()
const cartStore = useCartStore()

const totalPrice = computed(() => {
  let count = 0
  wishlistStore.wishlist.products.edges.forEach((el) => {
    count = count + el.node.price
  })

  return count
})
const productsInWishlist = computed(() => {
  return wishlistStore.wishlist.products ? wishlistStore.wishlist.products.edges : []
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})

const selectedProducts = ref([]);

const addToCart = async () => {
  const selectedProductsDetails = productsInWishlist.value
      .filter((_, index) => selectedProducts.value[index])
      .map((product) => ({
        uuid: product.node.uuid,
        name: product.node.name,
      }));

  for (const product of selectedProductsDetails) {
    const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, product.uuid);

    if (res.errors) {
      ElNotification({
        message: res.errors[0].message,
        type: "error"
      });
    } else {
      cartStore.setCurrentOrders(res.data.addOrderProduct.order)

      ElNotification({
        message: t('popup.addToCart', { product: product.name }),
        type: "success"
      });
    }
  }

  selectedProducts.value = [];
};

const removeProductFromWishlist = async (productUuid) => {
  const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, productUuid)

  if (!res.errors) {
    wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
  }
}
</script>

<style lang="scss" scoped>
.wishlist {
  &__inner {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__title {
    color: #F3F3F3;
    font-size: 40px;
    font-weight: 700;
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 25px;

    &-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 15px;
      border-bottom: 3px solid $white;

      & p {
        color: #F3F3F3;
        font-size: 20px;

        &:last-child {
          width: 128px;
        }
      }
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: 25px;
    }

    &-bottom {
      border-top: 3px solid $white;
      padding-top: 15px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      & div {
        display: flex;
        align-items: center;
        gap: 20px;

        & h6 {
          color: #F3F3F3;
          font-size: 20px;
          font-weight: 700;
          text-transform: uppercase;
        }

        & p {
          display: flex;
          align-items: center;
          gap: 5px;
          color: #F3F3F3;
          font-size: 20px;

          & img {
            width: 26px;
          }
        }
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;

    & div {
      display: flex;
      align-items: center;
      gap: 40px;
    }

    &-image {
      width: 165px;
      border-radius: 10px;
    }

    & h6 {
      color: #F3F3F3;
      font-size: 20px;
      font-weight: 700;
      text-transform: uppercase;
    }

    & p {
      display: flex;
      align-items: center;
      gap: 5px;
      color: #F3F3F3;
      font-size: 20px;

      & img {
        width: 32px;
      }
    }

    & svg {
      cursor: pointer;
      transition: 0.2s;
      color: $white;

      @include hover {
        color: #ff0000;
      }
    }
  }

  &__button {
    width: fit-content;
    padding-inline: 20px;
  }

  &__empty {
    margin-top: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 25px;

    & p {
      color: #F3F3F3;
      text-align: center;
      font-size: 40px;
      font-weight: 700;
    }

    & button {
      width: fit-content;
      padding-inline: 20px;
    }
  }
}

@media (max-width: 750px) {
  .wishlist {
    &__main {
      &-top {
        & p {
          &:last-child {
            width: 100px;
          }
        }
      }
    }

    &__item {
      &-image {
        width: 125px;
      }

      & h6 {
        font-size: 14px;
      }

      & div {
        gap: 15px;
      }

      & p {
        font-size: 16px;
      }
    }
  }
}

@media (max-width: 500px) {
  .wishlist {
    &__item {
      & div {
        &:first-child {
          flex-direction: column;
          align-items: flex-start;
          gap: 5px;
        }
      }
    }

    &__main {
      &-bottom {
        flex-direction: column;
        align-items: flex-start;
        gap: 25px;
      }
    }
  }
}
</style>